import React, { FC, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { FrozenSalesWeekly } from "interface/sales";
import { GrideDateFormatter } from "ui/Date";
import { FieldFull, FieldSplit } from "ui/CardField";
import { formatWeight, formatCurrency } from "utils/formatters"

interface FrozenWeeklySalesMobileListProps {
	items: FrozenSalesWeekly[];
	loading: boolean;
	onLoadMore: () => void;
}

const FrozenWeeklySalesMobileList: FC<FrozenWeeklySalesMobileListProps> = ({
	items,
	loading,
	onLoadMore,
}) => {
	const { t } = useTranslation();

	const empty = useMemo(
		() => !loading && items.length === 0,
		[items.length, loading],
	);

	useEffect(() => {
		const handleWindowScroll = () => {
			const remaining =
				document.documentElement.scrollHeight -
				window.innerHeight -
				window.scrollY;

			if (remaining < 200) {
				onLoadMore();
			}
		};

		window.addEventListener("scroll", handleWindowScroll, { passive: true });
		handleWindowScroll();

		return () => {
			window.removeEventListener("scroll", handleWindowScroll);
		};
	}, [onLoadMore, items.length, loading]);

	return (
		<div className="frozen-sales-list-mobile">
			{empty && (
				<div className="frozen-sales-list-mobile-empty">{t("NoRows")}</div>
			)}

			{items.map((item, index) => (
				<div
					key={`${item.vesselName || "noname"}-${index}`}
					className="frozen-sales-list-card"
				>
					<div className="frozen-sales-list-card-header">
						<div className="frozen-sales-list-card-group">
							{item.vesselName || "-"}
						</div>
						<div className="frozen-sales-list-card-date">
							{item.salesDate ? (
								<GrideDateFormatter
									date={item.salesDate as any}
									formatType="dd.MM.yyyy"
									noTd={true}
								/>
							) : (
								"-"
							)}
						</div>
					</div>

					<div className="frozen-sales-list-card-body">
						<FieldFull
							label={t("Product")}
							value={item.productText || "-"}
						/>
						<FieldSplit
							leftLabel={t("Weight")}
							leftValue={formatWeight(item.netLiveWeightKg)}
							rightLabel={t("AverageWeight")}
							rightValue={formatWeight(item.averagePrice, 2, false)}
						/>
						<FieldSplit
							leftLabel={t("Price")}
							leftValue={formatCurrency(item.price)}
							rightLabel="MSC"
							rightValue={item.msc || "-"}
						/>
						<FieldFull
							label={t("BuyerName")}
							value={item.buyerName || "-"}
						/>
						<FieldFull
							label={t("Information")}
							value={item.information}
							hideEmpty = { true }
						/>
						<FieldSplit
							leftLabel={t("WeekDay")}
							leftValue={item.salesWeekDay || "-"}
							rightLabel={t("Type")}
							rightValue={item.salesType || "-"}
						/>
					</div>
				</div>
			))}

			{loading && (
				<div className="frozen-sales-list-mobile-loading">{t("Loading")}</div>
			)}
		</div>
	);
};

export default FrozenWeeklySalesMobileList;